import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    Box,
    Typography,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableRow,
    TableHead
} from "@mui/material";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import GridOnIcon from "@mui/icons-material/GridOn";

export default function MicroWellCalibrationReport() {

    const { state } = useLocation();
    const navigate = useNavigate();

    const rawData = state?.runData;

    if (!rawData) return <Typography>No Report Data</Typography>;

    const description = rawData.description;
    const result = rawData.result || rawData?.resultData || {};
    const rows = rawData.rows || [];
    const parameters = rawData.parameters || {};

    /* PDF EXPORT */
    const exportPDF = () => {
        const doc = new jsPDF();

        doc.text("MicroWell Calibration Report", 14, 10);
        doc.text(`Description: ${description || "-"}`, 14, 26);

        let y = 35;

        /* RESULTS */
        autoTable(doc, {
            startY: y,
            head: [["Key", "Value"]],
            body: Object.entries(result).map(([k, v]) => [k, String(v)]),
            headStyles: {
                fillColor: [119, 77, 239],
                textColor: [255, 255, 255],
                fontStyle: "bold"
            }
        });

        y = doc.lastAutoTable.finalY + 10;

        /* CALIBRATION ROWS */
        autoTable(doc, {
            startY: y,
            head: [["Level", "Concentration", "ALU Replicates"]],
            body: rows.map((r, i) => [
                i + 1,
                r.conc,
                typeof r.resp === "string" ? r.resp : String(r.resp)
            ]),
            headStyles: {
                fillColor: [119, 77, 239],
                textColor: [255, 255, 255],
                fontStyle: "bold"
            }
        });

        y = doc.lastAutoTable.finalY + 10;

        /* PARAMETERS */
        const paramBody = Object.entries(parameters)
            .filter(([k]) => !k.startsWith("TXB") && k !== "afMGCCConcentration" && k !== "afMGCCSignal")
            .map(([k, v]) => [
                k,
                Array.isArray(v) ? v.join(", ") : String(v)
            ]);

        if (parameters?.afMGCCConcentration?.length) {
            const mgccPairs = parameters.afMGCCConcentration.map((c, i) =>
                `${c}, ${parameters.afMGCCSignal?.[i] ?? ""}`
            ).join(" | ");
            paramBody.push(["MGCC Curve Points", mgccPairs]);
        }

        if (paramBody.length > 0) {
            autoTable(doc, {
                startY: y,
                head: [["Parameter", "Value"]],
                body: paramBody,
                headStyles: {
                    fillColor: [119, 77, 239],
                    textColor: [255, 255, 255],
                    fontStyle: "bold"
                }
            });
        }

        doc.save(`microwell_calibration_${description}.pdf`);
    };

    /* ──────────────────── EXCEL EXPORT ──────────────────── */
    const exportExcel = () => {

        const wsData = [
            ["MicroWell Calibration Report"],
            [],

            ["Description", description],
            [],
            ["RESULTS"],
            ["Key", "Value"],
            ...Object.entries(result).map(([k, v]) => [k, v]),
            [],
            ["CALIBRATION DATA"],
            ["Level", "Concentration", "ALU Replicates"],
            ...rows.map((r, i) => [
                i + 1,
                r.conc,
                typeof r.resp === "string" ? r.resp : String(r.resp)
            ]),
            [],
            ["PARAMETERS"],
            ["Parameter", "Value"],
            ...Object.entries(parameters)
                .filter(([k]) => !k.startsWith("TXB") && k !== "afMGCCConcentration" && k !== "afMGCCSignal")
                .map(([k, v]) => [
                    k,
                    Array.isArray(v) ? v.join(", ") : v
                ])
        ];

        if (parameters?.afMGCCConcentration?.length) {
            const mgccPairs = parameters.afMGCCConcentration.map((c, i) =>
                `${c}, ${parameters.afMGCCSignal?.[i] ?? ""}`
            ).join(" | ");
            wsData.push(["MGCC Curve Points", mgccPairs]);
        }

        const ws = XLSX.utils.aoa_to_sheet(wsData);

        const range = XLSX.utils.decode_range(ws["!ref"]);

        for (let C = range.s.c; C <= range.e.c; ++C) {
            const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });

            if (!ws[cellAddress]) continue;

            ws[cellAddress].s = {
                fill: {
                    fgColor: { rgb: "#774DEF" }
                },
                font: {
                    bold: true,
                    color: { rgb: "FFFFFF" }
                },
                alignment: {
                    horizontal: "center"
                }
            };
        }

        ws["!cols"] = [
            { wch: 25 },
            { wch: 20 },
            { wch: 20 }
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Calibration Report");
        XLSX.writeFile(wb, `microwell_calibration_${description}.xlsx`);
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                <Typography variant="h5">MicroWell Calibration Report</Typography>
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate("/MicroWell")}
                >
                    Back to MicroWell
                </Button>
            </Box>

            {/* Export buttons */}
            <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
                <Button
                    variant="contained"
                    startIcon={<PictureAsPdfIcon />}
                    onClick={exportPDF}
                    sx={{ backgroundColor: "#774def", "&:hover": { backgroundColor: "#5e3bc7" } }}
                >
                    Export PDF
                </Button>
                <Button
                    variant="contained"
                    startIcon={<GridOnIcon />}
                    onClick={exportExcel}
                    sx={{ backgroundColor: "#774def", "&:hover": { backgroundColor: "#5e3bc7" } }}
                >
                    Export Excel
                </Button>
            </Box>

            {/* Description */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="body2">
                    <strong>Description:</strong> {description || "-"}
                </Typography>
            </Paper>

            {/* Results */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>Calibration Results</Typography>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                            <TableCell sx={{ fontWeight: 600 }}>Parameter</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {Object.entries(result).map(([k, v]) => (
                            <TableRow key={k}>
                                <TableCell sx={{ fontWeight: 500 }}>{k}</TableCell>
                                <TableCell>{String(v)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>

            {/* Calibration Data */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>Calibration Data (Input)</Typography>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                            <TableCell sx={{ fontWeight: 600 }}>Level</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Concentration</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>ALU Replicates</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((r, i) => (
                            <TableRow key={i}>
                                <TableCell>{i + 1}</TableCell>
                                <TableCell>{r.conc}</TableCell>
                                <TableCell>{typeof r.resp === "string" ? r.resp : String(r.resp)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>

            {/* Parameters */}
            <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Assay Parameters</Typography>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                            <TableCell sx={{ fontWeight: 600 }}>Parameter</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {Object.entries(parameters)
                            .filter(([k]) => !k.startsWith("TXB") && k !== "afMGCCConcentration" && k !== "afMGCCSignal")
                            .map(([k, v]) => (
                                <TableRow key={k}>
                                    <TableCell sx={{ fontWeight: 500 }}>{k}</TableCell>
                                    <TableCell>
                                        {Array.isArray(v) ? v.join(", ") : String(v)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        {parameters?.afMGCCConcentration?.length > 0 && (
                            <TableRow>
                                <TableCell sx={{ fontWeight: 500 }}>MGCC Curve Points</TableCell>
                                <TableCell>
                                    {parameters.afMGCCConcentration.map((c, i) =>
                                        `(${c}, ${parameters.afMGCCSignal?.[i] ?? ""})`
                                    ).join(" ")}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </Paper>
        </Box>
    );
}
